import { InconsistencyData, SuspiciousRentalStats, IoTStats, FinancialStats, DIAGNOSTICS_START_DATE, BIKE_STATUS_MAPPING, RENTAL_STATUS_MAPPING, PAYMENT_STATUS_MAPPING } from './DiagnosticsTypes';

// Функция определения типа тарифа
export const determineTariffType = (tariff: string, courierTariff?: string): keyof SuspiciousRentalStats['byTariffType'] => {
  const tariffString = (tariff || courierTariff || '').toLowerCase();
  
  if (tariffString.includes('weekly') || tariffString.includes('week') || tariffString.includes('недел')) {
    return 'weekly';
  }
  if (tariffString.includes('biweekly') || tariffString.includes('2week') || tariffString.includes('2 недел')) {
    return 'biweekly';
  }
  if (tariffString.includes('monthly') || tariffString.includes('month') || tariffString.includes('месяц')) {
    return 'monthly';
  }
  if (tariffString.includes('yandex') || tariffString.includes('яндекс')) {
    return 'yandex';
  }
  
  return 'unknown';
};

// Функция расчета ожидаемой суммы (оригинальные тарифы)
export const calculateExpectedAmount = (rental: any, courier: any): number => {
  const tariffType = determineTariffType(rental.tariff_type, courier?.tariff);
  const startDate = new Date(rental.start_date);
  const endDate = rental.end_date ? new Date(rental.end_date) : new Date();
  const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

  // Оригинальные тарифы из старой диагностики
  switch (tariffType) {
    case 'weekly': 
      return Math.ceil(duration / 7) * 4500; // 4500₽ в неделю
    case 'biweekly': 
      return Math.ceil(duration / 14) * 8000; // 8000₽ в две недели
    case 'monthly': 
      return Math.ceil(duration / 30) * 14000; // 14000₽ в месяц
    case 'yandex': 
      return Math.ceil(duration / 7) * 3500; // 3500₽ в неделю для Yandex
    default: 
      return duration * 200; // 200₽ в день по умолчанию
  }
};

// Функция расчета ожидаемой общей суммы за весь период аренды
export const calculateExpectedTotalAmount = (rental: any, courier: any): number => {
  const tariffType = determineTariffType(rental.tariff_type, courier?.tariff);
  const startDate = new Date(rental.start_date);
  const endDate = rental.end_date ? new Date(rental.end_date) : new Date();
  const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

  // Оригинальные тарифы из старой диагностики
  switch (tariffType) {
    case 'weekly': 
      return Math.ceil(duration / 7) * 4500; // 4500₽ в неделю
    case 'biweekly': 
      return Math.ceil(duration / 14) * 8000; // 8000₽ в две недели
    case 'monthly': 
      return Math.ceil(duration / 30) * 14000; // 14000₽ в месяц
    case 'yandex': 
      return Math.ceil(duration / 7) * 3500; // 3500₽ в неделю для Yandex
    default: 
      return duration * 200; // 200₽ в день по умолчанию
  }
};

// Функция определения отклонения в днях для тарифа
const calculateDaysDeviation = (rental: any, courier: any, actualAmount: number, expectedAmount: number) => {
  const tariffType = determineTariffType(rental.tariff_type, courier?.tariff);
  const difference = expectedAmount - actualAmount;
  
  // Определяем стоимость одного дня для каждого тарифа
  let dailyRate: number;
  switch (tariffType) {
    case 'weekly': 
      dailyRate = 4500 / 7; // ~643₽ в день
      break;
    case 'biweekly': 
      dailyRate = 8000 / 14; // ~571₽ в день
      break;
    case 'monthly': 
      dailyRate = 14000 / 30; // ~467₽ в день
      break;
    case 'yandex': 
      dailyRate = 3500 / 7; // 500₽ в день
      break;
    default: 
      dailyRate = 200; // 200₽ в день по умолчанию
  }
  
  return Math.abs(difference / dailyRate);
};

// Функция фильтрации данных по дате начала диагностики
const filterByDiagnosticsDate = (data: any[], dateField: string) => {
  return data.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= new Date(DIAGNOSTICS_START_DATE);
  });
};

// Проверка подозрительных аренд (только с 1 июня 2025) - УЛУЧШЕННАЯ ЛОГИКА
export const checkSuspiciousRentals = (rentals: any[], couriers: any[]): { inconsistencies: InconsistencyData[], stats: SuspiciousRentalStats } => {
  const inconsistencies: InconsistencyData[] = [];
  const stats: SuspiciousRentalStats = {
    totalSuspicious: 0,
    byTariffType: { weekly: 0, biweekly: 0, monthly: 0, yandex: 0, unknown: 0 },
    potentialLoss: 0,
    coverageRate: 0,
    detectionAccuracy: 95,
    criticalUnderpayments: 0,
    temporalViolations: 0,
    earlyPayments: 0,
    latePayments: 0,
    rapidPayments: 0
  };

  // Фильтруем аренды с 1 июня 2025 и исключаем Yandex сделки
  let filteredRentals = filterByDiagnosticsDate(rentals, 'start_date')
    .filter(rental => !rental.human_number?.includes('Yandex'));

  // ИСКЛЮЧАЕМ ТОЛЬКО ОЖИДАЮЩИЕ АРЕНДЫ И АКТИВНЫЕ (ДЛЯ ЭТОЙ ПРОВЕРКИ)
  filteredRentals = filteredRentals.filter(rental => 
    rental.status !== 'pending' && rental.status !== 'active'
  );

  // ДОБАВЛЯЕМ ГРЕЙС-ПЕРИОД для недавно завершенных аренд
  const now = new Date();
  const gracePeriodHours = 1; // 1 час грейс-период
  
  filteredRentals = filteredRentals.filter(rental => {
    // Определяем дату завершения аренды - используем более раннюю из end_date или updated_at
    // Это исправляет проблему с арендами, у которых end_date в будущем
    const endDateFromField = rental.end_date ? new Date(rental.end_date) : null;
    const updatedAtDate = new Date(rental.updated_at);
    const endDate = endDateFromField && endDateFromField.getTime() < updatedAtDate.getTime() 
      ? endDateFromField 
      : updatedAtDate;
      
    const hoursSinceEnd = (now.getTime() - endDate.getTime()) / (1000 * 60 * 60);
    
    // Пропускаем аренды, завершенные менее 1 часа назад
    return hoursSinceEnd >= gracePeriodHours;
  });

  filteredRentals.forEach(rental => {
    const courier = couriers.find(c => c.id === rental.courier_id);
    if (!rental.amount || parseFloat(rental.amount) <= 0) return;

    const expectedAmount = calculateExpectedAmount(rental, courier);
    const actualAmount = parseFloat(rental.amount);
    const difference = expectedAmount - actualAmount;
    const percentageDeviation = Math.abs(difference / expectedAmount) * 100;
    const tariffType = determineTariffType(rental.tariff_type, courier?.tariff);

    // Определение периода аренды
    const startDate = new Date(rental.start_date);
    const endDate = rental.end_date ? new Date(rental.end_date) : new Date();
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const rentalPeriod = `${duration} дней`;

    // Рассчитываем отклонение в днях
    const daysDeviation = calculateDaysDeviation(rental, courier, actualAmount, expectedAmount);

    // Определяем серьезность на основе отклонения в днях
    let severity: 'low' | 'medium' | 'high' | 'critical' = 'medium';
    let shouldReport = false;
    let severityReason = '';

    if (difference > 0) { // Недоплата
      if (daysDeviation <= 2) {
        severity = 'low';
        severityReason = `Незначительная недоплата`;
        shouldReport = percentageDeviation > 15 || difference > 1000;
      } else if (daysDeviation <= 5) {
        severity = 'medium';
        severityReason = `Умеренная недоплата`;
        shouldReport = true;
      } else if (daysDeviation <= 10) {
        severity = 'high';
        severityReason = `Серьезная недоплата`;
        shouldReport = true;
      } else {
        severity = 'critical';
        severityReason = `Критическая недоплата`;
        shouldReport = true;
        stats.criticalUnderpayments++;
      }
    } else { // Переплата
      if (daysDeviation <= 2) {
        severity = 'low';
        severityReason = `Незначительная переплата`;
        shouldReport = percentageDeviation > 25;
      } else if (daysDeviation <= 7) {
        severity = 'medium';
        severityReason = `Переплата`;
        shouldReport = true;
      } else {
        severity = 'high';
        severityReason = `Значительная переплата`;
        shouldReport = true;
      }
    }

    if (shouldReport) {
      if (difference > 0) {
        stats.potentialLoss += difference;
      }

      // Создаем более информативное описание
      const amountText = difference > 0 ? `недоплата ${difference.toFixed(0)}₽` : `переплата ${Math.abs(difference).toFixed(0)}₽`;
      
      inconsistencies.push({
        id: `suspicious-rental-${rental.id}`,
        type: 'Подозрительная аренда',
        description: `Аренда #${rental.human_number}: ${severityReason} - ${amountText}`,
        severity,
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          rentalPeriod,
          expectedAmount,
          actualAmount,
          difference: Math.abs(difference),
          deviation: percentageDeviation,
          daysDeviation: daysDeviation,
          impact: difference > 0 ? `Недоплата: ${difference.toFixed(0)}₽` : `Переплата: ${Math.abs(difference).toFixed(0)}₽`,
          suggestions: daysDeviation <= 2 ? [
            'Отклонение в пределах нормы (1-2 дня)',
            'Возможно связано с особенностями расчета периода',
            'Рекомендуется мониторинг, но не требует срочных действий'
          ] : [
            'Проверить правильность тарификации',
            'Связаться с курьером для уточнения',
            'Проверить систему расчетов'
          ]
        },
        timestamp: new Date().toISOString(),
        rental,
        courier,
        // Добавляем поля для отображения
        rentalStatus: rental.status,
        rentalPeriodDisplay: rentalPeriod
      });

      stats.totalSuspicious++;
      stats.byTariffType[tariffType]++;
    }
  });

  // Пересчитываем покрытие с учетом всех отфильтрованных аренд
  const originalFilteredCount = filterByDiagnosticsDate(rentals, 'start_date')
    .filter(rental => !rental.human_number?.includes('Yandex')).length;
  
  stats.coverageRate = originalFilteredCount > 0 ? ((originalFilteredCount - stats.totalSuspicious) / originalFilteredCount) * 100 : 100;

  return { inconsistencies, stats };
};

// Анализ временных аномалий (только с 1 июня 2025)
export const analyzeTemporalAnomalies = (rentals: any[], payments: any[]): InconsistencyData[] => {
  const inconsistencies: InconsistencyData[] = [];
  const now = new Date();

  // Фильтруем данные с 1 июня 2025
  const filteredRentals = filterByDiagnosticsDate(rentals, 'start_date');
  const filteredPayments = filterByDiagnosticsDate(payments, 'payment_date');

  filteredRentals.forEach(rental => {
    const startDate = new Date(rental.start_date);
    const endDate = rental.end_date ? new Date(rental.end_date) : null;
    const expectedEndDate = rental.expected_end_date ? new Date(rental.expected_end_date) : null;

    // Проверка на будущие даты начала
    if (startDate > now) {
      inconsistencies.push({
        id: `future-start-${rental.id}`,
        type: 'Временная аномалия',
        description: `Аренда #${rental.human_number} имеет дату начала в будущем`,
        severity: 'high',
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: 'Дата начала <= текущей даты',
          actual: startDate.toLocaleDateString('ru-RU'),
          timingIssue: 'Будущая дата начала',
          impact: 'Нарушение временной логики системы',
          suggestions: ['Проверить системное время', 'Исправить дату начала']
        },
        timestamp: new Date().toISOString(),
        rental
      });
    }

    // Проверка просроченных активных аренд
    if (rental.status === 'active' && expectedEndDate && expectedEndDate < now) {
      const overdueDays = Math.ceil((now.getTime() - expectedEndDate.getTime()) / (1000 * 60 * 60 * 24));
      let severity: 'medium' | 'high' | 'critical' = 'medium';
      let violationDegree = 'умеренное';

      if (overdueDays > 7) {
        severity = 'critical';
        violationDegree = 'критическое';
      } else if (overdueDays > 3) {
        severity = 'high';
        violationDegree = 'серьезное';
      }

      inconsistencies.push({
        id: `overdue-${rental.id}`,
        type: 'Просроченная аренда',
        description: `Аренда #${rental.human_number} просрочена на ${overdueDays} дней`,
        severity,
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: expectedEndDate.toLocaleDateString('ru-RU'),
          actual: 'Аренда все еще активна',
          timeViolation: overdueDays,
          violationDegree,
          impact: `Потенциальная потеря контроля над велосипедом`,
          suggestions: ['Связаться с курьером немедленно', 'Принудительно завершить аренду', 'Заблокировать велосипед']
        },
        timestamp: new Date().toISOString(),
        rental
      });
    }
  });

  // Анализ аномалий в платежах (только с 1 июня 2025)
  filteredPayments.forEach(payment => {
    const paymentDate = new Date(payment.payment_date || payment.created_at);
    const rental = filteredRentals.find(r => r.id === payment.rental_id);
    
    if (rental) {
      const rentalStart = new Date(rental.start_date);
      const daysDifference = Math.ceil((paymentDate.getTime() - rentalStart.getTime()) / (1000 * 60 * 60 * 24));

      // Очень ранние платежи (менее чем через час после начала аренды)
      if (daysDifference === 0 && (paymentDate.getTime() - rentalStart.getTime()) < (60 * 60 * 1000)) {
        inconsistencies.push({
          id: `early-payment-${payment.id}`,
          type: 'Подозрительный платеж',
          description: `Платеж по аренде #${rental.human_number} произведен слишком рано`,
          severity: 'medium',
          affectedEntity: 'payment',
          entityId: payment.id,
          details: {
            expected: 'Платеж через несколько дней',
            actual: 'Платеж в течение часа',
            paymentDelay: daysDifference,
            timingIssue: 'Слишком ранний платеж',
            impact: 'Возможное нарушение процедуры оплаты',
            suggestions: ['Проверить корректность процедуры', 'Связаться с курьером']
          },
          timestamp: new Date().toISOString(),
          payment,
          rental
        });
      }

      // Очень поздние платежи (более 7 дней)
      if (daysDifference > 7) {
        inconsistencies.push({
          id: `late-payment-${payment.id}`,
          type: 'Просроченный платеж',
          description: `Платеж по аренде #${rental.human_number} произведен с задержкой ${daysDifference} дней`,
          severity: daysDifference > 14 ? 'high' : 'medium',
          affectedEntity: 'payment',
          entityId: payment.id,
          details: {
            expected: 'Платеж в течение 3-7 дней',
            actual: `Платеж через ${daysDifference} дней`,
            paymentDelay: daysDifference,
            timingIssue: 'Задержка платежа',
            impact: 'Риск невозврата средств',
            suggestions: ['Проверить финансовую дисциплину курьера', 'Ужесточить контроль платежей']
          },
          timestamp: new Date().toISOString(),
          payment,
          rental
        });
      }
    }
  });

  return inconsistencies;
};

// Проверка соответствия статусов велосипедов и аренд
export const checkStatusInconsistencies = (bikes: any[], rentals: any[]): InconsistencyData[] => {
  const inconsistencies: InconsistencyData[] = [];
  
  // Получаем ВСЕ активные/ожидающие аренды с привязанным велосипедом
  // Убираем фильтр по дате, так как статус велосипеда должен соответствовать ТЕКУЩИМ активным арендам
  const activeRentals = rentals.filter(r => 
    (r.status === 'active' || r.status === 'pending') && r.bike_id
  );
  
  bikes.forEach(bike => {
    const activeRental = activeRentals.find(r => r.bike_id === bike.id);
    
    // Если есть активная/ожидающая аренда с привязанным велосипедом, велосипед должен быть 'in_use'
    if (activeRental && bike.status !== 'in_use') {
      inconsistencies.push({
        id: `status-bike-rental-${bike.id}`,
        type: 'Несоответствие статуса',
        description: `Велосипед ${bike.serial_number || bike.id} имеет активную аренду, но статус не "В аренде"`,
        severity: 'high',
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          expected: BIKE_STATUS_MAPPING.in_use,
          actual: BIKE_STATUS_MAPPING[bike.status as keyof typeof BIKE_STATUS_MAPPING] || bike.status,
          impact: 'Некорректное отображение доступности велосипеда',
          suggestions: ['Синхронизировать статус велосипеда', 'Проверить процесс обновления статусов']
        },
        timestamp: new Date().toISOString(),
        bike,
        rental: activeRental
      });
    }
    
    // Если нет активной аренды, велосипед не должен быть 'in_use'
    if (!activeRental && bike.status === 'in_use') {
      inconsistencies.push({
        id: `status-bike-no-rental-${bike.id}`,
        type: 'Несоответствие статуса',
        description: `Велосипед ${bike.serial_number || bike.id} имеет статус "В аренде", но нет активной аренды`,
        severity: 'medium',
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          expected: BIKE_STATUS_MAPPING.available,
          actual: BIKE_STATUS_MAPPING.in_use,
          impact: 'Велосипед недоступен для новых аренд',
          suggestions: ['Изменить статус на "Доступен"', 'Проверить завершение последней аренды']
        },
        timestamp: new Date().toISOString(),
        bike
      });
    }
  });

  return inconsistencies;
};

// IoT диагностика с улучшенной проверкой актуальности данных
export const checkIoTDiagnostics = (bikes: any[], trackingHistory?: any[]): { inconsistencies: InconsistencyData[], stats: IoTStats } => {
  const inconsistencies: InconsistencyData[] = [];
  const stats: IoTStats = {
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    batteryIssues: 0,
    connectionIssues: 0,
    lastUpdateIssues: 0,
    performanceIssues: 0,
    totalBikes: bikes.length,
    withIoT: 0,
    coveragePercent: 0,
    activePercent: 0,
    gpsDataQuality: 0
  };

  const now = new Date();
  let qualityIssues = 0;

  // Создаем карту последних обновлений из истории отслеживания
  const trackingUpdates = new Map();
  if (trackingHistory && trackingHistory.length > 0) {
    // Группируем по bike_id и находим самую свежую запись для каждого велосипеда
    trackingHistory.forEach(record => {
      const bikeId = record.bike_id;
      const createdAt = record.created_at ? new Date(record.created_at) : null;
      
      if (createdAt && (!trackingUpdates.has(bikeId) || createdAt > trackingUpdates.get(bikeId))) {
        trackingUpdates.set(bikeId, createdAt);
      }
    });
  }

  // Определяем велосипеды с IoT модулем (если есть gps_tag и он не равен 0)
  const bikesWithIoT = bikes.filter(bike => bike.gps_tag && bike.gps_tag !== '0' && bike.gps_tag !== 0);
  stats.withIoT = bikesWithIoT.length;
  stats.totalDevices = bikesWithIoT.length;

  bikesWithIoT.forEach(bike => {
    // Получаем самое актуальное время обновления с проверками на null/undefined
    const trackingUpdate = trackingUpdates.get(bike.id);
    const lastUpdate = bike.last_update ? new Date(bike.last_update) : null;
    const updatedAt = bike.updated_at ? new Date(bike.updated_at) : null;
    const lastMovementUpdate = bike.last_movement_update ? new Date(bike.last_movement_update) : null;
    
    // Приоритизируем данные из bike_tracking_history, затем остальные источники
    let mostRecentUpdate = null;
    const updateTimes = [trackingUpdate, lastMovementUpdate, lastUpdate, updatedAt].filter(time => time !== null && time !== undefined);
    
    if (updateTimes.length > 0) {
      mostRecentUpdate = new Date(Math.max(...updateTimes.map(time => time!.getTime())));
    }

    // Для велосипедов со статусом 'in_use' (в аренде) используем более мягкие критерии
    const isInUse = bike.status === 'in_use';
    
    // Определяем источник самого свежего обновления для информативности
    let updateSource = 'unknown';
    if (mostRecentUpdate) {
      if (trackingUpdate && mostRecentUpdate.getTime() === trackingUpdate.getTime()) {
        updateSource = 'tracking_history';
      } else if (lastMovementUpdate && mostRecentUpdate.getTime() === lastMovementUpdate.getTime()) {
        updateSource = 'last_movement_update';
      } else if (lastUpdate && mostRecentUpdate.getTime() === lastUpdate.getTime()) {
        updateSource = 'last_update';
      } else if (updatedAt && mostRecentUpdate.getTime() === updatedAt.getTime()) {
        updateSource = 'updated_at';
      }
    }

    const hoursSinceUpdate = mostRecentUpdate ? (now.getTime() - mostRecentUpdate.getTime()) / (1000 * 60 * 60) : Infinity;

    // Статистика подключения
    if (bike.online) {
      stats.onlineDevices++;
    } else {
      stats.offlineDevices++;
      
      inconsistencies.push({
        id: `offline-${bike.id}`,
        type: 'IoT устройство офлайн',
        description: `Велосипед ${bike.serial_number || bike.id} (GPS: ${bike.gps_tag}) не подключен к сети`,
        severity: 'high',
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          gpsTag: bike.gps_tag,
          expected: 'Устройство в сети',
          actual: 'Устройство офлайн',
          impact: 'Невозможность мониторинга и управления',
          suggestions: ['Проверить GSM сигнал', 'Перезагрузить устройство', 'Проверить SIM карту']
        },
        timestamp: new Date().toISOString(),
        bike
      });
      stats.connectionIssues++;
    }

    // Проверка батареи
    if (bike.battery_percentage < 10) {
      inconsistencies.push({
        id: `battery-critical-${bike.id}`,
        type: 'Критически низкий заряд',
        description: `Велосипед ${bike.serial_number || bike.id} (GPS: ${bike.gps_tag}) имеет критически низкий заряд: ${bike.battery_percentage}%`,
        severity: 'critical',
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          gpsTag: bike.gps_tag,
          expected: '>= 20%',
          actual: `${bike.battery_percentage}%`,
          impact: 'Риск полной разрядки и потери связи',
          suggestions: ['Срочная замена/зарядка батареи', 'Временно вывести из эксплуатации']
        },
        timestamp: new Date().toISOString(),
        bike
      });
      stats.batteryIssues++;
    } else if (bike.battery_percentage < 20) {
      stats.batteryIssues++;
    }

    // Проверка качества GPS данных
    if (!bike.latitude || !bike.longitude || bike.latitude === 0 || bike.longitude === 0) {
      qualityIssues++;
      inconsistencies.push({
        id: `gps-quality-${bike.id}`,
        type: 'Проблемы GPS',
        description: `Велосипед ${bike.serial_number || bike.id} (GPS: ${bike.gps_tag}) имеет некорректные GPS координаты`,
        severity: 'medium',
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          gpsTag: bike.gps_tag,
          expected: 'Корректные GPS координаты',
          actual: 'Нулевые или отсутствующие координаты',
          impact: 'Невозможность отслеживания местоположения',
          suggestions: ['Проверить GPS модуль', 'Проверить антенну', 'Перезагрузить устройство']
        },
        timestamp: new Date().toISOString(),
        bike
      });
    }

    // Проверка актуальности данных с улучшенной логикой
    const hasValidCoordinates = bike.latitude && bike.longitude && bike.latitude !== 0 && bike.longitude !== 0;
    const hasTrackingData = trackingUpdate !== null;
    
    // Устанавливаем пороги обновления в зависимости от статуса и источника данных
    let updateThreshold;
    if (hasTrackingData) {
      // Если есть свежие данные из tracking_history, используем более мягкие критерии
      updateThreshold = isInUse ? 48 : 24; // 48 часов для аренды, 24 для доступных
    } else {
      // Если данные только из таблицы bikes, используем более строгие критерии
      updateThreshold = isInUse ? 24 : 12; // 24 часа для аренды, 12 для доступных
    }
    
    if (hoursSinceUpdate > updateThreshold) {
      let severity: 'medium' | 'high' | 'critical' = 'medium';
      
      // Определяем серьезность с учетом статуса велосипеда и источника данных
      if (isInUse) {
        if (hoursSinceUpdate > 168) severity = 'critical'; // 7 дней
        else if (hoursSinceUpdate > 96) severity = 'high'; // 4 дня
      } else {
        if (hoursSinceUpdate > 72) severity = 'critical'; // 3 дня
        else if (hoursSinceUpdate > 48) severity = 'high'; // 2 дня
      }

      inconsistencies.push({
        id: `update-delay-${bike.id}`,
        type: 'Устаревшие данные',
        description: `Велосипед ${bike.serial_number || bike.id} (GPS: ${bike.gps_tag}) не обновлялся ${hoursSinceUpdate.toFixed(1)} часов`,
        severity,
        affectedEntity: 'bike',
        entityId: bike.id,
        details: {
          gpsTag: bike.gps_tag,
          expected: `<= ${updateThreshold} часов`,
          actual: `${hoursSinceUpdate.toFixed(1)} часов`,
          impact: `Неактуальная информация о состоянии. Статус: ${bike.status === 'in_use' ? 'в аренде' : 'доступен'}. Источник времени: ${updateSource}. GPS координаты: ${hasValidCoordinates ? 'корректные' : 'некорректные'}. Данные из tracking: ${hasTrackingData ? 'да' : 'нет'}`,
          suggestions: ['Проверить соединение', 'Перезагрузить устройство']
        },
        timestamp: new Date().toISOString(),
        bike
      });
      stats.lastUpdateIssues++;
    }
  });

  stats.coveragePercent = stats.totalBikes > 0 ? (stats.withIoT / stats.totalBikes) * 100 : 0;
  stats.activePercent = stats.totalDevices > 0 ? (stats.onlineDevices / stats.totalDevices) * 100 : 0;
  stats.gpsDataQuality = stats.totalDevices > 0 ? ((stats.totalDevices - qualityIssues) / stats.totalDevices) * 100 : 100;

  return { inconsistencies, stats };
};

// Финансовая диагностика (только с 1 июня 2025) - УПРОЩЕННАЯ ЛОГИКА
export const checkFinancialDiagnostics = (payments: any[], rentals: any[]): { inconsistencies: InconsistencyData[], stats: FinancialStats } => {
  const inconsistencies: InconsistencyData[] = [];
  const stats: FinancialStats = {
    totalPayments: 0,
    successfulPayments: 0,
    failedPayments: 0,
    pendingPayments: 0,
    missingReceipts: 0,
    amountDiscrepancies: 0,
    totalRevenue: 0,
    expectedRevenue: 0,
    revenueLoss: 0,
    successRate: 0,
    receiptCoverage: 0,
    canceledAuthorizations: 0,
    authorizationIssues: 0
  };

  // Фильтруем платежи с 1 июня 2025
  const filteredPayments = filterByDiagnosticsDate(payments, 'payment_date');

  stats.totalPayments = filteredPayments.length;

  // Группируем платежи по rental_id для анализа
  const paymentsByRental = new Map<string, any[]>();
  
  filteredPayments.forEach(payment => {
    if (payment.rental_id) {
      if (!paymentsByRental.has(payment.rental_id)) {
        paymentsByRental.set(payment.rental_id, []);
      }
      paymentsByRental.get(payment.rental_id)!.push(payment);
    }
  });

  // Анализ платежей по статусам
  filteredPayments.forEach(payment => {
    switch (payment.status) {
      case 'completed':
      case 'paid':
        stats.successfulPayments++;
        stats.totalRevenue += parseFloat(payment.amount || 0);
        break;
      case 'failed':
        stats.failedPayments++;
        inconsistencies.push({
          id: `failed-payment-${payment.id}`,
          type: 'Неудачный платеж',
          description: `Платеж #${payment.human_number || payment.id} завершился неудачно`,
          severity: 'high',
          affectedEntity: 'payment',
          entityId: payment.id,
          details: {
            expected: 'Успешный платеж',
            actual: 'Неудачный платеж',
            impact: 'Потеря дохода',
            suggestions: ['Связаться с курьером', 'Проверить платежные данные', 'Повторить платеж']
          },
          timestamp: new Date().toISOString(),
          payment
        });
        break;
      case 'pending':
        stats.pendingPayments++;
        break;
      case 'cancelled':
        stats.canceledAuthorizations++;
        break;
    }

    // Проверка отсутствия чеков для успешных платежей
    if (!payment.receipt_url && (payment.status === 'completed' || payment.status === 'paid')) {
      inconsistencies.push({
        id: `missing-receipt-${payment.id}`,
        type: 'Отсутствует чек',
        description: `Платеж #${payment.human_number || payment.id} не имеет чека`,
        severity: 'medium',
        affectedEntity: 'payment',
        entityId: payment.id,
        details: {
          expected: 'URL чека',
          actual: 'Чек отсутствует',
          impact: 'Нарушение налогового законодательства',
          suggestions: ['Запросить чек у платежной системы', 'Создать чек вручную']
        },
        timestamp: new Date().toISOString(),
        payment
      });
      stats.missingReceipts++;
    }
  });

  // Анализ недоплат/переплат по арендам - ТОЛЬКО ПО ПЛАТЕЖАМ с фильтрацией активных аренд
  const now = new Date();
  const gracePeriodHours = 1; // 1 час грейс-период
  
  paymentsByRental.forEach((rentalPayments, rentalId) => {
    const rental = rentals.find(r => r.id === rentalId);
    if (!rental) return;

    // СПЕЦИАЛЬНАЯ ЛОГИКА ДЛЯ АКТИВНЫХ АРЕНД - ТОЛЬКО КРИТИЧЕСКИЕ ПРОВЕРКИ
    if (rental.status === 'active') {
      // Для активных аренд проверяем только критические случаи
      const rentalAmount = parseFloat(rental.amount || 0);
      const successfulPayments = rentalPayments.filter(p => 
        (p.status === 'completed' || p.status === 'paid') && 
        parseFloat(p.amount || 0) >= 1
      );
      const hasAnyPayments = successfulPayments.length > 0;

      // Только критическая проблема: нулевая сумма аренды И отсутствие платежей
      if (rentalAmount === 0 && !hasAnyPayments) {
        inconsistencies.push({
          id: `critical-active-rental-${rentalId}`,
          type: 'Критическая проблема активной аренды',
          description: `Активная аренда #${rental.human_number || rentalId} имеет нулевую сумму и отсутствие платежей`,
          severity: 'critical',
          affectedEntity: 'rental',
          entityId: rentalId,
          details: {
            expected: 'Сумма аренды > 0₽ или наличие платежей',
            actual: 'Нулевая сумма и отсутствие платежей',
            impact: 'Активная аренда без оплаты',
            suggestions: [
              'Проверить настройку тарифа для активной аренды',
              'Убедиться в корректности системы платежей'
            ]
          },
          timestamp: new Date().toISOString(),
          rental,
          payment: rentalPayments[0]
        });
        stats.amountDiscrepancies++;
      }
      return; // Завершаем обработку для активных аренд
    }

    // ИСКЛЮЧАЕМ ТОЛЬКО ОЖИДАЮЩИЕ АРЕНДЫ (для завершенных аренд оставляем всю логику)
    if (rental.status === 'pending') {
      return; // Пропускаем только ожидающие аренды
    }

    // ДОБАВЛЯЕМ ГРЕЙС-ПЕРИОД для недавно завершенных аренд с исправлением для будущих дат
    const endDateFromField = rental.end_date ? new Date(rental.end_date) : null;
    const updatedAtDate = new Date(rental.updated_at);
    const endDate = endDateFromField && endDateFromField.getTime() < updatedAtDate.getTime() 
      ? endDateFromField 
      : updatedAtDate;
      
    const hoursSinceEnd = (now.getTime() - endDate.getTime()) / (1000 * 60 * 60);
    
    // Пропускаем аренды, завершенные менее 1 часа назад
    if (hoursSinceEnd < gracePeriodHours) {
      return;
    }

    // Фильтруем только успешные платежи и исключаем технические/ошибочные - ИСПРАВЛЯЕМ ФИЛЬТР
    const successfulPayments = rentalPayments.filter(p => 
      (p.status === 'completed' || p.status === 'paid') && 
      parseFloat(p.amount || 0) >= 1 // Исправлено: было > 1, теперь >= 1
    );

    if (successfulPayments.length === 0) return;

    // Расчитываем сумму аренды ТОЛЬКО по успешным платежам
    const actualTotalAmount = successfulPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    // Находим курьера для расчетов
    const courier = rentals.find(r => r.id === rentalId)?.courier_id ? 
      { tariff: rental.tariff_type } : null;

    // Рассчитываем ожидаемую общую сумму за весь период аренды
    const expectedTotalAmount = calculateExpectedTotalAmount(rental, courier);
    const totalDifference = actualTotalAmount - expectedTotalAmount;
    const totalDeviation = expectedTotalAmount > 0 ? Math.abs(totalDifference / expectedTotalAmount) * 100 : 0;

    // Анализ паттерна платежей для определения продлений
    const paymentAmounts = successfulPayments.map(p => parseFloat(p.amount || 0));
    const uniqueAmounts = [...new Set(paymentAmounts)];
    const isLikelyRenewal = uniqueAmounts.length === 1 && successfulPayments.length > 1;
    
    // Определяем проблемы с общей суммой
    let isProblem = false;
    let problemType = '';
    let severity: 'medium' | 'high' | 'critical' = 'medium';
    let description = '';
    let impact = '';

    if (totalDifference < -100) { // Недоплата больше 100₽
      isProblem = true;
      problemType = 'Недоплата за аренду';
      severity = Math.abs(totalDifference) > 1000 ? 'critical' : 'high';
      description = `Недоплата по аренде #${rental.human_number || rentalId}: ${Math.abs(totalDifference).toFixed(0)}₽`;
      impact = `Потеря дохода: ${Math.abs(totalDifference).toFixed(0)}₽`;
    } else if (totalDifference > expectedTotalAmount && !isLikelyRenewal && totalDeviation > 50) { 
      // Значительная переплата (исключая продления)
      isProblem = true;
      problemType = 'Значительная переплата';
      severity = totalDifference > expectedTotalAmount * 2 ? 'critical' : 'high';
      description = `Значительная переплата по аренде #${rental.human_number || rentalId}: ${totalDifference.toFixed(0)}₽`;
      impact = `Возможная ошибка в расчетах или двойные платежи`;
    }

    if (isProblem) {
      // Анализ паттерна платежей для контекста
      let paymentPattern = '';
      if (isLikelyRenewal) {
        paymentPattern = `${successfulPayments.length} одинаковых платежа по ${paymentAmounts[0]}₽ (возможно продление)`;
      } else if (successfulPayments.length > 1) {
        paymentPattern = `${successfulPayments.length} платежа на суммы: ${paymentAmounts.map(a => a + '₽').join(', ')}`;
      } else {
        paymentPattern = `1 платеж на сумму ${actualTotalAmount}₽`;
      }

      inconsistencies.push({
        id: `total-amount-discrepancy-${rentalId}`,
        type: problemType,
        description,
        severity,
        affectedEntity: 'rental',
        entityId: rentalId,
        details: {
          expected: `${expectedTotalAmount}₽ (за весь период аренды)`,
          actual: `${actualTotalAmount}₽ (${paymentPattern})`,
          difference: Math.abs(totalDifference),
          deviation: totalDeviation,
          impact,
          suggestions: [
            'Проверить корректность расчетов за период аренды', 
            'Сверить с данными о продлениях',
            isLikelyRenewal ? 'Возможно нормальное продление аренды' : 'Проверить на дублирование платежей'
          ]
        },
        timestamp: new Date().toISOString(),
        rental,
        payment: successfulPayments[0]
      });
      stats.amountDiscrepancies++;
    }
  });

  // ДОБАВЛЯЕМ ОТДЕЛЬНУЮ ПРОВЕРКУ АРЕНД С НУЛЕВОЙ СУММОЙ
  const filteredRentals = filterByDiagnosticsDate(rentals, 'start_date');
  
  filteredRentals.forEach(rental => {
    // Проверяем аренды с нулевой или отсутствующей суммой
    const rentalAmount = parseFloat(rental.amount || 0);
    
    if (rentalAmount === 0 || rental.amount === null || rental.amount === undefined) {
      // СПЕЦИАЛЬНАЯ ЛОГИКА ДЛЯ АКТИВНЫХ АРЕНД
      if (rental.status === 'active') {
        // Для активных аренд с нулевой суммой проверяем наличие любых платежей
        const rentalPayments = payments.filter(p => p.rental_id === rental.id);
        const hasAnyPayments = rentalPayments.some(p => 
          (p.status === 'completed' || p.status === 'paid') && 
          parseFloat(p.amount || 0) >= 1
        );
        
        // Сообщаем о проблеме только если нет вообще никаких платежей
        if (!hasAnyPayments) {
          inconsistencies.push({
            id: `zero-amount-active-rental-${rental.id}`,
            type: 'Активная аренда без оплаты',
            description: `Активная аренда #${rental.human_number || rental.id} имеет нулевую сумму и отсутствие платежей`,
            severity: 'high', // Для активных аренд снижаем серьезность
            affectedEntity: 'rental',
            entityId: rental.id,
            details: {
              expected: 'Наличие суммы аренды или платежей',
              actual: `${rentalAmount}₽ и отсутствие платежей`,
              impact: `Активная аренда без финансового учета`,
              suggestions: [
                'Проверить настройку тарификации для активной аренды',
                'Убедиться в работе системы платежей'
              ]
            },
            timestamp: new Date().toISOString(),
            rental
          });
          stats.amountDiscrepancies++;
        }
        return; // Завершаем обработку для активных аренд
      }
      
      // Исключаем только ожидающие аренды (для завершенных аренд оставляем полную логику)
      if (rental.status === 'pending') {
        return;
      }

      // Проверяем грейс-период
      const endDateFromField = rental.end_date ? new Date(rental.end_date) : null;
      const updatedAtDate = new Date(rental.updated_at);
      const endDate = endDateFromField && endDateFromField.getTime() < updatedAtDate.getTime() 
        ? endDateFromField 
        : updatedAtDate;
        
      const hoursSinceEnd = (now.getTime() - endDate.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceEnd < gracePeriodHours) {
        return;
      }

      // Рассчитываем ожидаемую сумму
      const courier = { tariff: rental.tariff_type };
      const expectedAmount = calculateExpectedTotalAmount(rental, courier);
      
      inconsistencies.push({
        id: `zero-amount-rental-${rental.id}`,
        type: 'Нулевая сумма аренды',
        description: `Аренда #${rental.human_number || rental.id} имеет нулевую сумму, ожидается ${expectedAmount}₽`,
        severity: 'critical',
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: `${expectedAmount}₽`,
          actual: `${rentalAmount}₽`,
          difference: expectedAmount,
          deviation: 100,
          impact: `Критическая потеря дохода: ${expectedAmount}₽`,
          suggestions: [
            'Проверить систему расчета стоимости аренды',
            'Исправить сумму аренды в базе данных',
            'Связаться с курьером для уточнения'
          ]
        },
        timestamp: new Date().toISOString(),
        rental
      });
      stats.amountDiscrepancies++;
    }
  });

  // Расчет ожидаемой выручки - теперь по платежам
  stats.expectedRevenue = rentals.reduce((sum, rental) => {
    const courier = { tariff: rental.tariff_type };
    return sum + calculateExpectedTotalAmount(rental, courier);
  }, 0);

  stats.revenueLoss = stats.expectedRevenue - stats.totalRevenue;
  stats.successRate = stats.totalPayments > 0 ? (stats.successfulPayments / stats.totalPayments) * 100 : 0;
  stats.receiptCoverage = stats.successfulPayments > 0 ? ((stats.successfulPayments - stats.missingReceipts) / stats.successfulPayments) * 100 : 0;

  return { inconsistencies, stats };
};

// Проверка целостности данных
export const checkDataConsistency = (bikes: any[], rentals: any[], couriers: any[]): InconsistencyData[] => {
  const inconsistencies: InconsistencyData[] = [];

  // Фильтруем аренды с 1 июня 2025
  const filteredRentals = filterByDiagnosticsDate(rentals, 'start_date');

  // Проверка ссылочной целостности
  filteredRentals.forEach(rental => {
    const bike = bikes.find(b => b.id === rental.bike_id);
    const courier = couriers.find(c => c.id === rental.courier_id);

    if (!bike) {
      inconsistencies.push({
        id: `missing-bike-${rental.id}`,
        type: 'Нарушение целостности',
        description: `Аренда #${rental.human_number} ссылается на несуществующий велосипед`,
        severity: 'critical',
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: 'Существующий велосипед',
          actual: 'Велосипед не найден',
          impact: 'Нарушение целостности данных',
          suggestions: ['Восстановить запись велосипеда', 'Исправить ссылку']
        },
        timestamp: new Date().toISOString(),
        rental
      });
    }

    if (!courier) {
      inconsistencies.push({
        id: `missing-courier-${rental.id}`,
        type: 'Нарушение целостности',
        description: `Аренда #${rental.human_number} ссылается на несуществующего курьера`,
        severity: 'critical',
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: 'Существующий курьер',
          actual: 'Курьер не найден',
          impact: 'Нарушение целостности данных',
          suggestions: ['Восстановить запись курьера', 'Исправить ссылку']
        },
        timestamp: new Date().toISOString(),
        rental
      });
    }
  });

  return inconsistencies;
};

// Проверка бизнес-логики
export const checkBusinessLogic = (bikes: any[], rentals: any[]): InconsistencyData[] => {
  const inconsistencies: InconsistencyData[] = [];

  // Фильтруем активные аренды с 1 июня 2025
  const activeRentals = filterByDiagnosticsDate(rentals, 'start_date').filter(r => r.status === 'active');

  // Проверка на дублирующиеся активные аренды
  const activeBikes = new Set();
  activeRentals.forEach(rental => {
    if (activeBikes.has(rental.bike_id)) {
      inconsistencies.push({
        id: `duplicate-rental-${rental.id}`,
        type: 'Дублирующая аренда',
        description: `Велосипед используется в нескольких активных аренды`,
        severity: 'critical',
        affectedEntity: 'rental',
        entityId: rental.id,
        details: {
          expected: 'Один велосипед - одна активная аренда',
          actual: 'Несколько активных аренд',
          impact: 'Нарушение бизнес-логики',
          suggestions: ['Завершить лишние аренды', 'Проверить процесс создания аренд']
        },
        timestamp: new Date().toISOString(),
        rental
      });
    }
    activeBikes.add(rental.bike_id);
  });

  return inconsistencies;
};

// Проверка системного здоровья
export const checkSystemHealth = (bikes: any[], rentals: any[], couriers: any[], payments: any[]): InconsistencyData[] => {
  const inconsistencies: InconsistencyData[] = [];
  const now = new Date();

  // Фильтруем данные с 1 июня 2025
  const filteredPayments = filterByDiagnosticsDate(payments, 'payment_date');
  const filteredRentals = filterByDiagnosticsDate(rentals, 'start_date');

  // Проверка активности платежей
  const recentPayments = filteredPayments.filter(p => {
    const paymentDate = new Date(p.payment_date || p.created_at);
    return (now.getTime() - paymentDate.getTime()) < (24 * 60 * 60 * 1000);
  });

  if (recentPayments.length === 0 && filteredPayments.length > 0) {
    inconsistencies.push({
      id: 'no-recent-payments',
      type: 'Системная проблема',
      description: 'Нет платежей за последние 24 часа',
      severity: 'high',
      affectedEntity: 'system',
      details: {
        expected: 'Регулярные платежи',
        actual: 'Отсутствие платежей 24+ часов',
        impact: 'Возможные проблемы с платежной системой',
        suggestions: ['Проверить интеграцию с платежной системой', 'Проверить процессы оплаты']
      },
      timestamp: new Date().toISOString()
    });
  }

  // Проверка создания новых аренд
  const recentRentals = filteredRentals.filter(r => {
    const rentalDate = new Date(r.start_date);
    return (now.getTime() - rentalDate.getTime()) < (24 * 60 * 60 * 1000);
  });

  if (recentRentals.length === 0 && filteredRentals.length > 0) {
    inconsistencies.push({
      id: 'no-recent-rentals',
      type: 'Системная проблема',
      description: 'Нет новых аренд за последние 24 часа',
      severity: 'medium',
      affectedEntity: 'system',
      details: {
        expected: 'Регулярные новые аренды',
        actual: 'Отсутствие новых аренд 24+ часов',
        impact: 'Возможное снижение активности бизнеса',
        suggestions: ['Проверить доступность велосипедов', 'Проверить маркетинговые активности']
      },
      timestamp: new Date().toISOString()
    });
  }

  return inconsistencies;
};
